import { MainLayout } from "@/components/layout/MainLayout";
import { QueueSummary } from "@/components/dashboard/QueueSummary";
import { DoctorsList } from "@/components/doctors/DoctorsList";
import { StatusTabs } from "@/components/queue/StatusTabs";
import { ManagementBlocks } from "@/components/dashboard/ManagementBlocks";
import { useAuthStore } from "@/lib/auth";
import { useQueueStore } from "@/lib/store";
import { AnalyticsSummaryCards } from "@/components/analytics/AnalyticsSummaryCards";
import { startOfMonth, endOfMonth } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useEffect, useState } from "react";
import { getSmartFilteredPatients } from "@/lib/utils/patientFiltering";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, Shield } from "lucide-react";
import { forceLogoutInactiveUsers, checkCurrentUserStatus } from "@/utils/forceLogoutInactiveUsers";
import { useToast } from "@/hooks/use-toast";

const Index = () => {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';
  const isStaff = user?.role === 'staff';
  const { toast } = useToast();
  
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const { 
    getRevenueSummary, 
    getPatientCountByDoctor,
    getCompletedAppointmentsByDate,
    fetchPatients,
    patients,
    isLoading,
    error
  } = useQueueStore();
  
  useEffect(() => {
    if (isAdmin || isStaff) {
      const loadData = async () => {
        try {
          await fetchPatients();
        } catch (error) {
          console.error('Error loading patients:', error);
        } finally {
          setIsInitialLoad(false);
        }
      };
      loadData();
      
      // Set up periodic refresh every 30 seconds for real-time updates
      const interval = setInterval(() => {
        console.log('Index: Refreshing patient data...');
        fetchPatients();
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [isAdmin, isStaff, fetchPatients]);

  // Get smart filtered patients - simplified for staff dashboard
  const filteredPatients = getSmartFilteredPatients(patients, {
    showAll: false,
    daysBack: 2,
    includeActive: true,
    includeFollowUps: false, // Disable follow-ups for staff dashboard
    includePendingPayments: false // Disable pending payments for staff dashboard
  });

  // Get financial data (still uses all patients for accurate analytics)
  const { 
    totalRevenue, 
    doctorRevenue, 
    dateRevenue,
    monthlyTrend
  } = getRevenueSummary();

  // Calculate current month's revenue
  const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM format
  const currentMonthData = monthlyTrend.find(month => 
    month.month === new Date(currentMonth + '-01').toLocaleString('default', { month: 'short', year: 'numeric' })
  );
  const monthlyRevenue = currentMonthData?.revenue || 0;
  
  const appointmentsByDate = getCompletedAppointmentsByDate(
    startOfMonth(new Date()), 
    endOfMonth(new Date())
  );
  
  // Calculate average daily patients
  const averageDailyPatients = appointmentsByDate.length 
    ? (appointmentsByDate.reduce((sum, item) => sum + item.count, 0) / appointmentsByDate.length)
    : 0;

  // Calculate today's revenue
  const today = new Date().toISOString().split('T')[0];
  const todayRevenue = dateRevenue
    .filter(item => item.date === today)
    .reduce((sum, item) => sum + item.revenue, 0);

  // Handle refresh functionality
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await fetchPatients();
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleCheckInactiveUsers = async () => {
    try {
      const inactiveUsers = await forceLogoutInactiveUsers();
      if (inactiveUsers && inactiveUsers.length > 0) {
        toast({
          title: "Inactive Users Found",
          description: `Found ${inactiveUsers.length} inactive users. They should be logged out automatically.`,
        });
      } else {
        toast({
          title: "No Inactive Users",
          description: "All users are currently active.",
        });
      }
    } catch (error) {
      toast({
        title: "Error Checking Users",
        description: "Failed to check user status.",
        variant: "destructive",
      });
    }
  };

  // Show loading state for admin and staff
  if ((isAdmin || isStaff) && (isInitialLoad || isLoading)) {
    return (
      <MainLayout>
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-bold">
              {isAdmin ? 'Admin Dashboard' : 'Staff Dashboard'}
            </h1>
            <p className="text-muted-foreground">
              Welcome back, {user?.name}
            </p>
          </div>
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading dashboard data...</p>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Show error state for admin and staff
  if ((isAdmin || isStaff) && error) {
    return (
      <MainLayout>
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-bold">
              {isAdmin ? 'Admin Dashboard' : 'Staff Dashboard'}
            </h1>
            <p className="text-muted-foreground">
              Welcome back, {user?.name}
            </p>
          </div>
          <div className="text-center py-12">
            <div className="text-red-600 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Dashboard</h3>
            <p className="text-gray-500 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {isAdmin ? (
          <div className="space-y-6">
            {/* Admin Dashboard Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold">Admin Dashboard</h1>
                <p className="text-muted-foreground">
                  Recent patients (last 2 days + active + follow-ups)
                </p>
              </div>
              <Button
                variant="outline"
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="flex items-center gap-2"
              >
                {isRefreshing ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                ) : (
                  <Eye className="h-4 w-4" />
                )}
                {isRefreshing ? 'Refreshing...' : 'Refresh'}
              </Button>
            </div>

            {/* Queue Summary Section - Top */}
            <Card>
              <CardHeader>
                <CardTitle>Queue Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <QueueSummary patients={filteredPatients} />
              </CardContent>
            </Card>

            {/* Analytics Section - Middle */}
            <Card>
              <CardHeader>
                <CardTitle>Analytics</CardTitle>
              </CardHeader>
              <CardContent>
                <AnalyticsSummaryCards 
                  totalRevenue={totalRevenue}
                  doctorName={doctorRevenue[0]?.doctorName.replace('Dr. ', '') || 'N/A'}
                  doctorRevenue={doctorRevenue[0]?.revenue || 0}
                  averageDailyPatients={averageDailyPatients}
                  appointmentsCount={dateRevenue.length}
                  todayRevenue={todayRevenue}
                  monthlyRevenue={monthlyRevenue}
                />
              </CardContent>
            </Card>

            {/* Doctors Section - Below Analytics */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Doctors</span>
                  <span className="text-sm text-muted-foreground">
                    {doctorRevenue.length} active doctors
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <DoctorsList />
              </CardContent>
            </Card>

            {/* Management Section - Bottom */}
            <Card>
              <CardHeader>
                <CardTitle>Management</CardTitle>
              </CardHeader>
              <CardContent>
                <ManagementBlocks />
              </CardContent>
            </Card>
          </div>
        ) : isStaff ? (
          <div className="space-y-6">
            {/* Staff Dashboard Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold">Staff Dashboard</h1>
                <p className="text-muted-foreground">
                  Welcome back, {user?.name}
                </p>
                <p className="text-sm text-muted-foreground">
                  Recent patients (last 2 days + active)
                </p>
              </div>
              <Button
                variant="outline"
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="flex items-center gap-2"
              >
                {isRefreshing ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                ) : (
                  <Eye className="h-4 w-4" />
                )}
                {isRefreshing ? 'Refreshing...' : 'Refresh'}
              </Button>
            </div>

            {/* Queue Summary Section */}
            <Card>
              <CardHeader>
                <CardTitle>Queue Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <QueueSummary patients={filteredPatients} />
              </CardContent>
            </Card>

            {/* Patient Queue Section */}
            <Card>
              <CardHeader>
                <CardTitle>Patient Queue</CardTitle>
              </CardHeader>
              <CardContent>
                <StatusTabs filteredPatients={filteredPatients} />
              </CardContent>
            </Card>

            {/* Doctors Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Available Doctors</span>
                  <span className="text-sm text-muted-foreground">
                    {doctorRevenue.length} active doctors
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <DoctorsList />
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="text-center">
              <h1 className="text-2xl font-bold">Welcome to AMCare</h1>
              <p className="text-muted-foreground">Please contact your administrator for access</p>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default Index;
